import {Component,Inject} from "@angular/core";
import {MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,MatLegacyDialogRef as MatDialogRef} from "@angular/material/legacy-dialog";
import {AutocompleteOptions} from "@share/component/autocomplete/autocomplete";
import {TranslateService} from "@ngx-translate/core";
import {Filter,TypeComparaison,TypeFilter,FilterValue} from "@domain/common/list-view";
import {SearchType} from "@domain/common/list-view/sorting";
import {EntityAttribute} from "@share/component/autocomplete/options/entity-attribute";
import {ToastrService} from "ngx-toastr";

/**
 * Popup d'association d'une prestation à une règle d'attribution
 */
@Component({
    selector: 'indemnites-regle-attribution-prestation-add',
    templateUrl: './indemnites-regle-attribution-prestation-add.component.html'
})
export class IndemnitesRegleAttributionPrestationAddComponent {
    /** Filtre de recherche des prestations */
    filtrePrestation: any = {
        idPays: 0,
        idTypeEntite: 0,
        //Conserve une structure de filtre homogène avec le composant autocomplete
        typeObjet: '',
        idObjet: 0
    };

    /** Libellé du domaine de la prestation sélectionnée */
    domainePrestation: string = '';

    /** Objet de lien règle / prestation à retourner */
    reglePresta: any;

    /** Options de l'autocomplete prestation */
    prestationOptions: AutocompleteOptions;

    constructor(private matDialogRef: MatDialogRef<IndemnitesRegleAttributionPrestationAddComponent>,
                private translateService: TranslateService,
                private toastrService: ToastrService,
                @Inject(MAT_DIALOG_DATA) public data: { regleAttribution: any }) {
        //Affichage du message V9 tant qu'aucune prestation n'est sélectionnée
        this.domainePrestation = this.translateService.instant('admin.frais.indemnites.reglesAttribution.detail.prestations.popup.selectionnerPrestation');

        //Initialisation du lien en cohérence avec le legacy V9
        this.reglePresta = {
            id: 0,
            bonus: null,
            qte: 1,
            prestation: null,
            regleAttribution: this.data?.regleAttribution
        };

        //Initialisation des options de recherche prestation pour fiabiliser le format de retour
        this.prestationOptions = IndemnitesRegleAttributionPrestationAddComponent.buildAutocompleteOptions(translateService);
    }

    /**
     * Construit les options de recherche d'une prestation pour une règle d'attribution
     *
     * @param translateService service de traduction
     * @param listeDomaines liste optionnelle des domaines de prestation pour le filtre select
     * @returns options de l'autocomplete de recherche de prestation
     */
    static buildAutocompleteOptions(translateService: TranslateService,listeDomaines?: FilterValue[]): AutocompleteOptions {
        return {
            //Endpoint IJ dédié au cas d'usage règle d'attribution (indépendant du contexte NDF/OD)
            url: () => `/controller/Prestation/searchPrestationsRegleAttribution`,
            //Permet de déclencher une recherche dès l'ouverture de la popup (même sans saisie utilisateur)
            isSearchWhenNoFilter: true,
            //Affichage continu des résultats sans flèches de pagination dans cette popup uniquement
            isLocal: false,
            isInfiniteScroll: true,
            searchType: SearchType.STARTS_WITH,
            displayItem: (item: any) => item?.libelle || '',
            listeFilters: (ts: TranslateService,filter?: any): Array<Filter> => {
                const filters: Array<Filter> = [{
                    title: ts.instant('filter.libelle'),
                    clef: 'libelleNettoye',
                    typeComparaison: TypeComparaison[TypeComparaison.LIKE],
                    isDefault: true
                }];

                //Le filtre select Domaine de prestation n'est pas proposé quand un domaine est déjà imposé par la règle
                if (listeDomaines?.length > 0 && !filter?.idDomainePrestation) {
                    filters.push({
                        title: ts.instant('admin.frais.indemnites.reglesAttribution.detail.prestations.colonnes.domaine'),
                        clef: 'idDomainePrestation',
                        type: TypeFilter[TypeFilter.LONG],
                        typeComparaison: TypeComparaison[TypeComparaison.EQUAL],
                        listeValues: listeDomaines,
                        isDefault: false
                    });
                }

                return filters;
            },
            listeStaticFilters: (filter?: any): Array<Filter> => {
                //Quand un domaine est déjà connu, on applique un filtre caché pour ne proposer que les prestations de ce domaine
                if (filter?.idDomainePrestation) {
                    return [{
                        clef: 'idDomainePrestation',
                        valeur: String(filter.idDomainePrestation),
                        type: TypeFilter[TypeFilter.LONG],
                        typeComparaison: TypeComparaison[TypeComparaison.EQUAL]
                    }];
                }

                return [];
            },
            listDisplayedAttributes: (item: any,ts: TranslateService): Array<EntityAttribute> => [{
                clef: 'admin.frais.indemnites.reglesAttribution.detail.prestations.colonnes.domaine',
                valeur: item?.domainePrestation || '-'
            }],
            mapRequest: (searchSpec: any) => {
                if (!searchSpec?.listeFilter?.length) {
                    return searchSpec;
                }

                //Normalisation des filtres texte vides pour déclencher une recherche large côté backend
                searchSpec.listeFilter = searchSpec.listeFilter.map((filter: any) => {
                    if (!filter) {
                        return filter;
                    }

                    if ((filter.clef === 'libelle' || filter.clef === 'libelleNettoye')
                        && (filter.valeur === null || filter.valeur === undefined || String(filter.valeur).trim() === '')) {
                        return {...filter,valeur: '%'};
                    }

                    return filter;
                });

                return searchSpec;
            },
            mapResult: (result: any,isAutocomplete?: boolean) => {
                const source = result?.data || result;
                const listeBrute = source?.listeResultats || source?.data || source?.listePrestation || (Array.isArray(source) ? source : []);
                const listeResultats = listeBrute || [];

                //Retour attendu par l'autocomplete inline
                if (isAutocomplete) {
                    return listeResultats;
                }

                //Retour attendu par la popup de recherche (ListView)
                return {
                    numPage: source?.numPage ?? 0,
                    nbPagesTotal: source?.nbPagesTotal ?? source?.nbPages ?? 1,
                    nbObjetsParPage: source?.nbObjetsParPage ?? source?.objetParPage ?? listeResultats.length,
                    nbObjetsDansPage: source?.nbObjetsDansPage ?? listeResultats.length,
                    nbObjetsTotal: source?.nbObjetsTotal ?? source?.nbObjets ?? source?.countTotal ?? listeResultats.length,
                    listeResultats
                };
            }
        };
    }

    /**
     * Vérifie qu'une prestation est bien associée à un domaine de prestation
     */
    private hasDomainePrestation(prestation: any): boolean {
        if (!prestation) {
            return false;
        }

        //Compatibilité formats legacy/V10 selon endpoint: identifiant domaine parfois sous plusieurs clés
        const idDomaine = prestation.idDomainePrestation || prestation.id_domaine_prestation || prestation.idDomainePrest;
        if (idDomaine && Number(idDomaine) > 0) {
            return true;
        }

        //Fallback legacy: certains retours n'exposent que le libellé domaine
        return !!(prestation.domainePrestation && String(prestation.domainePrestation).trim().length > 0);
    }

    /**
     * Mise à jour des données dérivées lors de la sélection d'une prestation
     */
    onPrestationChange(): void {
        this.domainePrestation = this.reglePresta?.prestation?.domainePrestation || this.translateService.instant('admin.frais.indemnites.reglesAttribution.detail.prestations.popup.domaineNonRenseigne');
        //La quantité d'une prestation associée à une règle d'attribution est fixée à 1 comme en V9
        this.reglePresta.qte = 1;
    }

    /**
     * Fermeture sans ajout
     */
    onCancel(): void {
        this.matDialogRef.close();
    }

    /**
     * Validation de l'ajout
     */
    onValidate(): void {
        //Une prestation doit être sélectionnée pour valider
        if (!this.reglePresta?.prestation) {
            return;
        }

        //Les prestations sans domaine ne peuvent pas être associées à une règle d'attribution
        if (!this.hasDomainePrestation(this.reglePresta.prestation)) {
            this.toastrService.error(this.translateService.instant('admin.frais.indemnites.reglesAttribution.detail.prestations.popup.erreurDomaineObligatoire'));
            return;
        }

        this.matDialogRef.close(this.reglePresta);
    }
}
